import { Component, type ErrorInfo, type ReactNode } from "react";
import { log } from "../lib/log";
import Wordmark from "./Wordmark";

// Top-level render-error boundary. A thrown error anywhere in the tree would
// otherwise unmount the whole app to a blank page; here we catch it, log it
// (always — this is a real failure), and show a minimal branded fallback with a
// reload action. Reusing the app chrome (wordmark + tool-name) keeps the crash
// screen recognizably part of the product. Class component because error
// boundaries have no hooks equivalent.
interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    log.error("Unhandled render error:", error, info.componentStack);
  }

  private reload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="auth-screen">
        <header className="topbar">
          <div className="topbar-brand">
            <span className="wordmark" aria-label="Incode">
              <Wordmark />
            </span>
            <span className="topbar-divider" aria-hidden="true" />
            <span className="tool-name">Omni Hub</span>
          </div>
        </header>

        <div className="auth-body">
          <div className="auth-card" role="alert">
            <h1 className="auth-title">Something went wrong</h1>
            <div className="auth-sub">
              The page hit an unexpected error. Reloading usually fixes it.
            </div>
            <button className="auth-btn primary block" onClick={this.reload}>
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
